import {
  Button,
  Divider,
  Flex,
  HStack,
  Select,
  Tbody,
  Td,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  Table,
  Th,
  Input,
  Box,
  Text,
  useToast,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import Card from 'components/Card/Card';
import { useEffect, useState } from 'react';
import ResultsController from './controllers/ResultsController';

function ResultsNew() {
  const [data, setData] = useState([]);
  const [audits, setAudits] = useState([]);
  const [selectedAudit, setSelectedAudit] = useState({ id: '', name: '' });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [modalAuditName, setModalAuditName] = useState('');
  const [modalStartDate, setModalStartDate] = useState('');
  const [modalEndDate, setModalEndDate] = useState('');
  const [modalContactStartDate, setModalContactStartDate] = useState('');
  const [modalContactEndDate, setModalContactEndDate] = useState('');
  const [agentNames, setAgentNames] = useState([]);
  const [modalAgent, setModalAgent] = useState('');
  const [modalSupervisor, setModalSupervisor] = useState('');
  const [modalTabulation, setmodalTabulation] = useState('');
  const [supervisorNames, setSupervisorNames] = useState([]);
  const [tabulationNames, setTabulationNames] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [modalQuestion, setModalQuestion] = useState('');
  const [modalAnswer, setModalAnswer] = useState('');

  const jwtToken = localStorage.getItem("access_token");
  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");

  // ------------------------ FUNÇÕES ------------------------

  const fetchData = async (
    auditName,
    startDate,
    endDate,
    agentName,
    supervisorName,
    tabulationName,
    questionName,
    answerName,
    contactStart,
    contactEnd
  ) => {
    try {
      const response = await ResultsController.listResults(
        jwtToken,
        auditName,
        startDate,
        endDate,
        agentName,
        supervisorName,
        tabulationName,
        questionName,
        answerName,
        contactStart,
        contactEnd
      );
      if (!response) throw new Error('Network response was not ok');
      setData(response);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Erro ao buscar dados",
        description: "Não existem dados disponíveis para os filtros informados.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right"
      });
      setData([]);
    }
  };

  const fetchAudits = async () => {
    const response = await ResultsController.listauditsActive(jwtToken);
    if (!response) return;
    setAudits(response);
  };

  const fetchQuestions = async (auditId, startDate, endDate) => {
    try {
      const response = await ResultsController.getQuestions(jwtToken, auditId, startDate, endDate);
      const list = Array.isArray(response) ? response : (response?.result || []);
      setQuestions(list);
    } catch (error) {
      console.error("Erro ao buscar perguntas:", error);
      setQuestions([]);
    }
  };

  const fetchAnswers = async (auditId, startDate, endDate) => {
    try {
      const response = await ResultsController.getAnswers(jwtToken, auditId, startDate, endDate);
      const list = Array.isArray(response) ? response : (response?.result || []);
      setAnswers(list);
    } catch (error) {
      console.error("Erro ao buscar respostas:", error);
      setAnswers([]);
    }
  };

  // ------------------------ HANDLERS ------------------------

  const handleAuditSelect = (e) => {
    const selectedName = e.target.value;
    setSelectedAudit({ id: '', name: selectedName });
    if (!selectedName) setQuestions([]);
  };

  const handleModalAuditSelect = (e) => setModalAuditName(e.target.value);

  const handleAdvancedOpen = () => {
    setModalAuditName(selectedAudit.name || '');
    setModalStartDate(startDate || '');
    setModalEndDate(endDate || '');
    setModalContactStartDate('');
    setModalContactEndDate('');
    setIsAdvancedOpen(true);
  };

  const handleAdvancedSearch = () => {
    // Somente Ficha, Data Início e Data Fim são obrigatórios
    if (!modalAuditName || !modalStartDate || !modalEndDate) {
      toast({
        title: "Campos obrigatórios",
        description: "Selecione ficha, data início e data fim para continuar.",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "top-right"
      });
      return;
    }
    if (modalStartDate > modalEndDate) {
      toast({
        title: "Data inválida",
        description: "A data de início não pode ser maior que a data de fim.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right"
      });
      return;
    }

    fetchData(
      modalAuditName,
      modalStartDate,
      modalEndDate,
      modalAgent,
      modalSupervisor,
      modalTabulation,
      modalQuestion,
      modalAnswer,
      modalContactStartDate,
      modalContactEndDate
    );

    setStartDate(modalStartDate);
    setEndDate(modalEndDate);
    setSelectedAudit({ id: '', name: modalAuditName });
    setIsAdvancedOpen(false);
  };

  // ------------------------ EFFECTS ------------------------

  useEffect(() => { fetchAudits(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const agents = await ResultsController.getAgentNames(jwtToken);
        setAgentNames(Array.isArray(agents) ? agents : (agents?.result || []));
      } catch (e) { console.error('Erro ao buscar agentes:', e); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const supervisors = await ResultsController.getSupervisorNames(jwtToken);
        setSupervisorNames(Array.isArray(supervisors) ? supervisors : (supervisors?.result || []));
      } catch (e) { console.error('Erro ao buscar supervisores:', e); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const tabulations = await ResultsController.getTabulationNames(jwtToken);
        setTabulationNames(Array.isArray(tabulations) ? tabulations : (tabulations?.result || []));
      } catch (e) { console.error('Erro ao buscar tabulações:', e); }
    })();
  }, []);

  useEffect(() => {
    if (modalAuditName && modalStartDate && modalEndDate) {
      const audit = audits.find(a => a.audit_name === modalAuditName);
      if (audit) {
        fetchQuestions(audit.audit_id, modalStartDate, modalEndDate);
        fetchAnswers(audit.audit_id, modalStartDate, modalEndDate);
      }
    }
  }, [modalAuditName, modalStartDate, modalEndDate, audits]);

  // ------------------------ RENDER ------------------------

  return (
    <>
      <Flex direction='column' height="100%" width="100%">
        <Card overflowX={{ sm: "scroll", xl: "hidden" }} height="100%">
          <VStack align="stretch" spacing={4} p={5} height="100%">
            <Flex direction="column" spacing={4} width="100%">
              <HStack spacing={6} mb={4} alignItems="flex-end" flexWrap="nowrap">
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>Ficha:</Text>
                  <Select
                    placeholder="Selecionar ficha"
                    value={selectedAudit.name}
                    onChange={handleAuditSelect}
                    width="240px"
                  >
                    {audits.map((audit, idx) => (
                      <option key={idx} value={audit.audit_name}>{audit.audit_name}</option>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>Data Início:</Text>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} width="170px" />
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>Data Fim:</Text>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} width="170px" />
                </Box>
                <Box>
                  <Button onClick={handleAdvancedOpen} variant="outline" size="lg" leftIcon={<SearchIcon />} height="44px" px={7}>
                    Buscar
                  </Button>
                </Box>
              </HStack>
            </Flex>

            {(data || []).map((audit, index) => (
              <Flex direction='column' mb={4} key={index} width="100%">
                <Flex mb={3} fontSize="xl" fontWeight="bold" color={textColor}>
                  Nome da Ficha: {audit.audit_name}
                </Flex>
                {audit.questions.map((question, qIndex) => (
                  <Flex direction='column' mb={3} key={qIndex} width="100%">
                    <Flex mb={2} alignItems="center" justifyContent="space-between">
                      <Flex alignItems="center">
                        <Flex fontSize="lg" fontWeight="bold" color={textColor} mr={2}>
                          Pergunta: {question.question_name}
                        </Flex>
                      </Flex>
                    </Flex>
                    <Flex width="100%" overflowX="auto">
                      <Table variant='simple' color={textColor} width="100%" size="sm">
                        <Thead>
                          <Tr>
                            <Th width="60%">Resposta Resumida</Th>
                            <Th textAlign="center" width="20%">Quantidade</Th>
                            <Th textAlign="center" width="20%">Porcentagem</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {question.responses.map((response, rIndex) => (
                            <Tr key={rIndex}>
                              <Td p={2} width="60%">{response.resposta_resumida}</Td>
                              <Td p={2} textAlign="center" width="20%">{response.count}</Td>
                              <Td p={2} textAlign="center" width="20%">{response.percentage.toFixed(2)}%</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Flex>
                  </Flex>
                ))}
                {index < data.length - 1 && <Divider mt={4} mb={4} />}
              </Flex>
            ))}
          </VStack>
        </Card>
      </Flex>

      {/* ------------------------ MODAL FILTROS ------------------------ */}
      <Modal isOpen={isAdvancedOpen} onClose={() => setIsAdvancedOpen(false)} size="xl" scrollBehavior="inside">
        <ModalContent>
          <ModalHeader>Filtros adicionais</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={2}>
              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>Ficha</Text>
                <Select placeholder="Selecionar ficha" value={modalAuditName} onChange={handleModalAuditSelect}>
                  {audits.map((audit, idx) => (
                    <option key={idx} value={audit.audit_name}>{audit.audit_name}</option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>Data Início</Text>
                <Input type="date" value={modalStartDate} onChange={(e) => setModalStartDate(e.target.value)} />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>Data Fim</Text>
                <Input type="date" value={modalEndDate} onChange={(e) => setModalEndDate(e.target.value)} />
              </Box>

              {/* Campos opcionais */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>Data Contato Início</Text>
                <Input type="date" value={modalContactStartDate} onChange={(e) => setModalContactStartDate(e.target.value)} />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>Data Contato Fim</Text>
                <Input type="date" value={modalContactEndDate} onChange={(e) => setModalContactEndDate(e.target.value)} />
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>Perguntas</Text>
                <Select placeholder="Selecionar pergunta" value={modalQuestion} onChange={(e) => setModalQuestion(e.target.value)}>
                  {questions.map((item, idx) => {
                    const value = typeof item === 'string' ? item : (item?.question_name || '');
                    const label = typeof item === 'string' ? item : (item?.question_name || 'Sem nome');
                    return <option key={`${value}-${idx}`} value={value}>{label}</option>;
                  })}
                </Select>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>Respostas</Text>
                <Select placeholder="Selecionar resposta" value={modalAnswer} onChange={(e) => setModalAnswer(e.target.value)}>
                  {answers.map((item, idx) => {
                    const value = typeof item === 'string' ? item : (item?.resposta_resumida || '');
                    const label = typeof item === 'string' ? item : (item?.resposta_resumida || 'Sem nome');
                    return <option key={`${value}-${idx}`} value={value}>{label}</option>;
                  })}
                </Select>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>Agente</Text>
                <Select placeholder="Selecionar agente" value={modalAgent} onChange={(e) => setModalAgent(e.target.value)}>
                  {agentNames.map((item, idx) => {
                    const value = typeof item === 'string' ? item : (item?.name || item?.agent_name || '');
                    const label = typeof item === 'string' ? item : (item?.name || item?.agent_name || 'Sem nome');
                    return <option key={`${value}-${idx}`} value={value}>{label}</option>;
                  })}
                </Select>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>Supervisor</Text>
                <Select placeholder="Selecionar supervisor" value={modalSupervisor} onChange={(e) => setModalSupervisor(e.target.value)}>
                  {supervisorNames.map((item, idx) => {
                    const value = typeof item === 'string' ? item : (item?.supervisor_name || '');
                    const label = typeof item === 'string' ? item : (item?.supervisor_name || 'Sem nome');
                    return <option key={`${value}-${idx}`} value={value}>{label}</option>;
                  })}
                </Select>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>Tabulação</Text>
                <Select placeholder="Selecionar tabulação" value={modalTabulation} onChange={(e) => setmodalTabulation(e.target.value)}>
                  {tabulationNames.map((item, idx) => {
                    const value = typeof item === 'string' ? item : (item?.tabulation || '');
                    const label = typeof item === 'string' ? item : (item?.tabulation || 'Sem nome');
                    return <option key={`${value}-${idx}`} value={value}>{label}</option>;
                  })}
                </Select>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={() => setIsAdvancedOpen(false)}>Cancelar</Button>
            <Button colorScheme="blue" onClick={handleAdvancedSearch}>Buscar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default ResultsNew;
